import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createItemSource,
  deleteItemSource,
  listCatalogSources,
  listItemSources,
  refreshItemSource,
} from '@/api/sources'
import type { CreateCatalogItemSourceRequest } from '@/api/types'

const SOURCES_QUERY_KEY = ['catalog-sources'] as const
const ITEM_SOURCES_QUERY_KEY = ['catalog-item-sources'] as const

const itemSourcesKey = (kind: 'MATERIAL' | 'TOOL', globalId: string) =>
  [...ITEM_SOURCES_QUERY_KEY, kind, globalId] as const

export const useCatalogSources = () =>
  useQuery({
    queryKey: SOURCES_QUERY_KEY,
    queryFn: listCatalogSources,
    // Source list rarely changes (seeded by migration); cache aggressively.
    staleTime: 5 * 60 * 1000,
  })

export const useItemSources = (kind: 'MATERIAL' | 'TOOL', globalId: string | null | undefined) =>
  useQuery({
    queryKey: globalId ? itemSourcesKey(kind, globalId) : ['catalog-item-sources', 'disabled'],
    queryFn: () => (globalId ? listItemSources(kind, globalId) : Promise.resolve([])),
    enabled: !!globalId,
  })

export const useCreateItemSource = (kind: 'MATERIAL' | 'TOOL', globalId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateCatalogItemSourceRequest) => createItemSource(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: itemSourcesKey(kind, globalId) })
      // The project detail's `liveSource` field changes when bindings change; invalidate the
      // project tree so the BOM picks up the new source on next render.
      qc.invalidateQueries({ queryKey: ['projects', 'detail'] })
    },
  })
}

export const useRefreshItemSource = (kind: 'MATERIAL' | 'TOOL', globalId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => refreshItemSource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: itemSourcesKey(kind, globalId) })
      qc.invalidateQueries({ queryKey: ['projects', 'detail'] })
    },
  })
}

export const useDeleteItemSource = (kind: 'MATERIAL' | 'TOOL', globalId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteItemSource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: itemSourcesKey(kind, globalId) })
      qc.invalidateQueries({ queryKey: ['projects', 'detail'] })
    },
  })
}
